import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { View, FlatList, StyleSheet, Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { HeaderBackButton } from '@react-navigation/elements';
import * as hotelActions from '../store/actions/hotel';
import { Screens, Modals } from '../constants/ScreenNames';
import { scale } from 'react-native-size-matters';
import { Color, CommonStyles } from '../styles';
import {
  selectHotelBrands,
  selectHotelSearchType,
  selectHotelFilterBy,
  selectHotelOrderBy,
  selectHotelsFiltered,
  selectHotelSearchResults,
  selectConvertedHotelMatrixData,
} from '../store/selectors/hotel.selector';
import { HotelCard } from '../components/resultCards';
import TabBar from '../components/SegmentedControl/TabBar';
// import FloatButton from '../components/FloatButton';
import SearchFabMenuModal from '../components/SearchFabMenuModal';
import {
  selectSearchCriteriaByComponentIndex,
  selectRequestComponentByComponentIndex,
} from '../store/selectors/search.selector';
import { hoteBrandMap } from '../constants/BrandMaps';
import BaseText from '../components/BaseText';
import { movePrevious } from '../store/actions/workflow';
import useBoolean from '../utils/useBoolean';
import { isCloseToBottom } from '../utils/screen.utils';
import HeaderTitle from '../components/shared/HeaderTitle';
import NavigateFilter from '../components/NavigateFilter';
import { rightFilterHeaderOption } from '../utils/defaultNavOptions';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import MapView from 'react-native-maps';
import InfoMarker, { HotelMarkerContent } from '../components/maps/InfoMarker';
import Interstitial from '../components/modals/Interstitial';
import useTextInterstitialOptions from '../utils/useTextInterstitialOptions';
import { Source, searchTypesEnum } from '../constants/Enums';
import BottomSheet from 'react-native-simple-bottom-sheet';
import useAvailableHeight from '../utils/useAvailableHeight';
import useTextOrderByHotelList from '../utils/useTextOrderByHotelList';
import CircleSegmentedControl from '../components/SegmentedControl/CircleSegmentedControl';
import { useDateFormat } from '../utils/useDateFormat';
import {
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  selectTokenData,
} from '../store/selectors/auth.selector';
import Messages from '../components/event/Messages';
import { selectTezEvent, selectTezEventGroup } from '../store/selectors/events.selector';
import { pageKeys } from '../constants/PageKeys';
import { EVENTS_ENABLED } from '../config/env';

const MAX_REQUEST_ATTEMPS = 7;

const HotelScreen = ({ navigation }) => {
  const { t } = useTranslation(i18nNS.hotel);
  const { tKey } = useComponentTKey('HotelScreen');
  const dispatch = useDispatch();
  const panelRef = useRef(null);
  const hotelResponses = useSelector(selectHotelsFiltered);
  const hotelBrandFilters = useSelector(selectHotelBrands);
  hotelBrandFilters?.map((item) => {
    item.name = t(tKey(item.key));
    item.desc = t(tKey(item.key + 'Desc'));
    return item;
  });
  const hotelSearchType = useSelector(selectHotelSearchType);
  const searchCriteria = useSelector(selectSearchCriteriaByComponentIndex);
  const requestComponent = useSelector(selectRequestComponentByComponentIndex);
  const searchResults = useSelector(selectHotelSearchResults);
  const orderBy = useSelector(selectHotelOrderBy);
  const filterBy = useSelector(selectHotelFilterBy);
  const matrixData = useSelector(selectConvertedHotelMatrixData);
  const [modalVisible, showModal, hideModal] = useBoolean(false);
  const [visibleNavigateFilter, showNavigateFilter, hideNavigateFilter] = useBoolean(false);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [visibleMenuButton, showMenuButton, hideMenuButton] = useBoolean(true);
  const interstitialOptions = useTextInterstitialOptions(searchTypesEnum.HOTEL);
  const flatListRef = useRef();
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);

  const iosMinimizeBtnHeight = 2;
  const availableHeight = useAvailableHeight(scale(iosMinimizeBtnHeight));
  const [orderByHotelList] = useTextOrderByHotelList();
  const formatDate = useDateFormat();
  let fromDate = '';
  let todate = '';

  if (requestComponent.DisplayDate) {
    const displaydateSeperated = requestComponent.DisplayDate?.split('-');
    fromDate = displaydateSeperated[0];
    todate = displaydateSeperated[1];
  }

  const onSearchFabMenuModalPress = (value) => {
    switch (value) {
      case 0:
        navigation.navigate(Modals.HotelFilterModal);
        break;
    }
    hideModal();
  };

  const setSelectedFilter = useCallback(
    (value) => {
      dispatch(hotelActions.filterBySearchType(value));
    },
    [dispatch]
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: (headerLeftProps) => (
        <HeaderBackButton {...headerLeftProps} onPress={() => dispatch(movePrevious())} />
      ),
      headerTitle: () => (
        <HeaderTitle
          title={
            hotelLoading
              ? t(tKey('Loading'))
              : hotelSearchType
              ? t(tKey(hoteBrandMap[hotelSearchType]?.key))
              : t(tKey('NoResults'))
          }
          subtitle={
            formatDate(fromDate, null, 'MMM DD') + ' - ' + formatDate(todate, null, 'MMM DD')
          }
          onPress={hotelSearchType && showNavigateFilter}
        />
      ),
      ...rightFilterHeaderOption(
        () => hotelSearchType && navigation.navigate(Modals.HotelFilterModal)
      ),
      headerTitleAlign: 'center',
    });
  }, [
    navigation,
    dispatch,
    showNavigateFilter,
    hotelSearchType,
    searchCriteria,
    hotelLoading,
    t,
    tKey,
  ]);

  useEffect(() => {
    async function fetchHotels() {
      setHotelLoading(true);
      await dispatch(hotelActions.hotelSearch(MAX_REQUEST_ATTEMPS));
      setHotelLoading(false);
    }
    fetchHotels();
  }, [dispatch]);

  useEffect(() => {
    if (hotelResponses && flatListRef.current) {
      flatListRef.current.scrollToOffset({ animated: true, offset: 0 });
    }

    showMenuButton();
  }, [hotelResponses, showMenuButton]);

  const selectHotel = (hotelResponse) => {
    // old app was not applying any UI change to supressed hotel, just preventing selection.
    if (!hotelResponse.IsSuppressHotel) {
      dispatch(hotelActions.selectHotel(hotelResponse));
      navigation.push(Screens.HotelDetail);
    }
  };

  return (
    <View style={styles.pageContainer}>
      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.HOTEL_LIST}
      />
      <View style={styles.wrapper}>
        <View style={CommonStyles.flex1}>
          {hotelLoading ? (
            <Interstitial icon="hotel" options={interstitialOptions} />
          ) : (
            <>
              {hotelResponses && searchResults ? (
                <MapView
                  style={styles.mapStyle}
                  initialRegion={{
                    latitude: searchResults.CityCenterLatitude,
                    longitude: searchResults.CityCenterLongitude,
                    latitudeDelta: 0.05,
                    longitudeDelta: 0.05,
                  }}
                >
                  {hotelResponses.map((hotelResponse, index) => {
                    return (
                      <InfoMarker
                        key={index}
                        latitude={Number(hotelResponse?.Latitude)}
                        longitude={Number(hotelResponse?.Longitude)}
                        label={'$' + parseInt(hotelResponse.price)}
                        onCalloutPress={() => selectHotel(hotelResponse)}
                      >
                        <HotelMarkerContent
                          hotelResponse={hotelResponse}
                          onPress={() => selectHotel(hotelResponse)}
                        />
                      </InfoMarker>
                    );
                  })}
                </MapView>
              ) : null}
              <BottomSheet
                ref={(ref) => (panelRef.current = ref)}
                wrapperStyle={{
                  backgroundColor: 'transparent',
                  paddingHorizontal: 0,
                  elevation: 0,
                }}
                lineStyle={{ padding: 0, marginTop: 12, marginBottom: 12 }}
                sliderMaxHeight={availableHeight}
                sliderMinHeight={scale(120)}
              >
                {(onScrollEndDrag) => (
                  <>
                    <CircleSegmentedControl
                      items={orderByHotelList}
                      onChange={(value) => dispatch(hotelActions.orderBy(value))}
                      selected={orderBy}
                    />
                    <FlatList
                      ref={flatListRef}
                      contentContainerStyle={styles.flexGrow1}
                      data={hotelResponses}
                      keyExtractor={(item) => item.HotelId}
                      renderItem={({ item }) => (
                        <HotelCard
                          style={CommonStyles.boxShadow}
                          item={item}
                          currency={currency}
                          currencyCulture={currencyCulture}
                          onPress={() => selectHotel(item)}
                        />
                      )}
                      ListEmptyComponent={() => <BaseText>{t(tKey('NoHotelsAvailable'))}</BaseText>}
                      onScroll={({ nativeEvent }) => {
                        if (isCloseToBottom(nativeEvent)) {
                          hideMenuButton();
                        } else if (!visibleMenuButton) {
                          showMenuButton();
                        }
                      }}
                      onScrollEndDrag={onScrollEndDrag}
                    />
                  </>
                )}
              </BottomSheet>
            </>
          )}
        </View>
        {/* {visibleMenuButton && <FloatButton onPress={showModal} iconName="menu" />} */}
        <SearchFabMenuModal
          title={t(tKey('FilterHotels'))}
          onPress={onSearchFabMenuModalPress}
          onClose={hideModal}
          visible={modalVisible}
        />
        <NavigateFilter
          onRequestClose={hideNavigateFilter}
          onPress={setSelectedFilter}
          selected={hotelSearchType}
          items={hotelBrandFilters}
          visible={visibleNavigateFilter}
        />
      </View>
    </View>
  );
};

export const styles = StyleSheet.create({
  mapStyle: {
    ...StyleSheet.absoluteFillObject,
  },
  pageContainer: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  wrapper: {
    height: '100%',
    justifyContent: 'space-between',
  },
  flexGrow1: {
    flexGrow: 1,
  },
  iosStyle: { height: 80, paddingBottom: 20 },
});

export default HotelScreen;
